import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function PurchaseOrderNew() {
  const navigate = useNavigate();
  
  const [po, setPo] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_date: '',
    warehouse_id: '',
    notes: '',
    status: 'draft'
  });
  
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [quickQuantities, setQuickQuantities] = useState({});
  
  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchWarehouses();
  }, []);
  
  const fetchSuppliers = async () => {
    try {
      const res = await apiFetch('/api/suppliers');
      const data = await res.json();
      setSuppliers(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setLoading(false);
    }
  };
  
  const fetchProducts = async () => {
    try {
      const res = await apiFetch('/api/products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  
  const fetchWarehouses = async () => {
    try {
      const res = await apiFetch('/api/warehouses');
      const data = await res.json();
      setWarehouses(data);
      
      // Set default warehouse
      const defaultWarehouse = data.find(w => w.is_default);
      if (defaultWarehouse) {
        setPo(prev => ({ ...prev, warehouse_id: defaultWarehouse.id }));
      }
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };
  
  const handleSave = async () => {
    // Validation
    if (!po.supplier_id) {
      window.alert('Please select a supplier');
      return;
    }
    
    if (items.length === 0) {
      window.alert('Please add at least one item');
      return;
    }
    
    if (!po.warehouse_id) {
      window.alert('Please select a warehouse');
      return;
    }
    
    setSaving(true);
    
    try {
      // Calculate total
      const total = items.reduce((sum, item) => 
        sum + (parseFloat(item.quantity) * parseFloat(item.unit_price)), 0
      );
      
      const payload = {
        ...po,
        total_amount: total,
        items: items
      };
      
      const res = await apiFetch('/api/purchase-orders', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (data.id) {
        window.alert(`Purchase Order ${data.po_number} created successfully`);
        navigate(`/purchase-orders/${data.id}`);
      }
    } catch (err) {
      console.error('Error creating PO:', err);
      window.alert('Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };
  
  const addProduct = (product) => {
    const qty = quickQuantities[product.id] || 1;
    
    // Check if product already in list
    const existingIndex = items.findIndex(item => item.product_id === product.id);
    
    if (existingIndex >= 0) {
      // Update quantity
      const updated = [...items];
      updated[existingIndex].quantity += qty;
      setItems(updated);
    } else {
      // Add new item
      setItems([...items, {
        product_id: product.id,
        product_name: product.name,
        product_code: product.code,
        quantity: qty,
        unit_price: product.cost_price || 0,
        supplier_code: product.supplier_code || ''
      }]);
    }
    
    // Reset quick quantity
    setQuickQuantities(prev => ({ ...prev, [product.id]: 1 }));
    setShowProductSelector(false);
    setProductSearch('');
  };
  
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };
  
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };
  
  const calculateTotal = () => {
    return items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)), 0
    );
  };
  
  // Filter products for selector
  const filteredProducts = products.filter(p => {
    const matchesSearch = !productSearch || 
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.code?.toLowerCase().includes(productSearch.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
  
  const selectedSupplier = suppliers.find(s => s.id === parseInt(po.supplier_id));
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Fixed Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/purchase-orders')}
            className="text-white hover:text-blue-100 transition"
          >
            ⬅ Back
          </button>
          <h1 className="text-4xl font-bold text-center flex-1">Create Purchase Order</h1>
          <div className="w-20"></div>
        </div>
      </div>
      
      {/* Form Content */}
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Basic Details */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <span className="text-2xl mr-2">📋</span>
            Order Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Supplier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier *
              </label>
              <select
                value={po.supplier_id}
                onChange={(e) => setPo({ ...po, supplier_id: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select supplier...</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.supplier_name}</option>
                ))}
              </select>
              {selectedSupplier && (
                <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  <div><strong>Contact:</strong> {selectedSupplier.contact_name || 'N/A'}</div>
                  <div><strong>Email:</strong> {selectedSupplier.email || 'N/A'}</div>
                  <div><strong>Phone:</strong> {selectedSupplier.phone || 'N/A'}</div>
                </div>
              )}
            </div>
            
            {/* Warehouse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse *
              </label>
              <select
                value={po.warehouse_id}
                onChange={(e) => setPo({ ...po, warehouse_id: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select warehouse...</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.name} {w.is_default ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Order Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Date *
              </label>
              <input
                type="date"
                value={po.order_date}
                onChange={(e) => setPo({ ...po, order_date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Expected Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Delivery Date
              </label>
              <input
                type="date"
                value={po.expected_date}
                onChange={(e) => setPo({ ...po, expected_date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={po.notes}
              onChange={(e) => setPo({ ...po, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Internal notes about this order..."
            />
          </div>
        </div>
        
        {/* Items Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <span className="text-2xl mr-2">📦</span>
              Items
            </h2>
            <button
              onClick={() => setShowProductSelector(!showProductSelector)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
            >
              {showProductSelector ? '✕ Close' : '+ Add Product'}
            </button>
          </div>
          
          {/* Product Selector */}
          {showProductSelector && (
            <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-200 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Code</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Category</th>
                      <th className="text-right px-3 py-2">Cost Price</th>
                      <th className="text-center px-3 py-2">Qty</th>
                      <th className="text-center px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product, idx) => (
                      <tr 
                        key={product.id}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="px-3 py-2">{product.code || '-'}</td>
                        <td className="px-3 py-2">{product.name}</td>
                        <td className="px-3 py-2">{product.category || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          £{parseFloat(product.cost_price || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            min="1"
                            value={quickQuantities[product.id] || 1}
                            onChange={(e) => setQuickQuantities({
                              ...quickQuantities,
                              [product.id]: parseInt(e.target.value) || 1
                            })}
                            className="w-16 border border-gray-300 rounded px-2 py-1 text-center"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => addProduct(product)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                          >
                            Add
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredProducts.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No products found
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Items Table */}
          {items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-left px-3 py-2">Supplier Code</th>
                    <th className="text-right px-3 py-2">Quantity</th>
                    <th className="text-right px-3 py-2">Unit Price</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-center px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr 
                      key={idx}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}
                    >
                      <td className="px-3 py-2">{item.product_code || '-'}</td>
                      <td className="px-3 py-2">{item.product_name}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.supplier_code}
                          onChange={(e) => updateItem(idx, 'supplier_code', e.target.value)}
                          placeholder="Supplier code..."
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 1)}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-right"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-right"
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        £{(parseFloat(item.quantity) * parseFloat(item.unit_price)).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => removeItem(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-100 font-bold">
                    <td colSpan="5" className="px-3 py-2 text-right">Total:</td>
                    <td className="px-3 py-2 text-right">£{calculateTotal().toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No items added yet</p>
              <p className="text-sm">Click "Add Product" to add items to this purchase order</p>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate('/purchase-orders')}
            className="bg-gray-300 hover:bg-gray-400 text-black px-6 py-2 rounded transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded transition disabled:bg-gray-400"
          >
            {saving ? 'Creating...' : 'Create Purchase Order'}
          </button>
        </div>
        
      </div>
    </div>
  );
}

export default PurchaseOrderNew;
